/**
 * UI 메시지 관련 유틸 함수 모듈
 * @module browser/ui
 */
import { i18n } from '@/common/locale';
import { ComponentCustomProperties } from 'vue';

/**
 * @deprecated use useMessage
 */
export const confirmMsg = (
  ctx: ComponentCustomProperties & Record<string, any>,
  {
    msgStr,
    okCallback,
    cancelCallback,
    title,
    useHTML,
    confirmBtnText = i18n.global.t('WORD.OK'),
    showClose = false,
    showConfirmBtn = true,
    showCancelBtn = true,
  }: {
    msgStr: string;
    okCallback?: () => void;
    cancelCallback?: () => void;
    title?: string;
    useHTML?: boolean;
    showClose?: boolean;
    confirmBtnText?: string;
    showConfirmBtn?: boolean;
    showCancelBtn?: boolean;
  },
): void => {
  ctx.$messagebox({
    message: msgStr,
    title,
    useHTML,
    closeOnClickModal: false,
    showClose,
    showConfirmBtn,
    showCancelBtn,
    focusable: true,
    onClose: (type) => {
      if (type === 'ok') {
        okCallback?.();
      } else if (typeof cancelCallback === 'function') {
        cancelCallback?.();
      }
    },
    confirmBtnText,
    cancelBtnText: i18n.global.t('WORD.CANCEL'),
  });
};

/**
 * @deprecated use useMessage
 */
export const promiseConfirmMsg = (...args: Parameters<typeof confirmMsg>): Promise<boolean> => {
  const [ctx, options] = args;
  return new Promise((resolve) => {
    confirmMsg(ctx, {
      ...options,
      okCallback: () => {
        options.okCallback?.();
        resolve(true);
      },
      cancelCallback: () => {
        options.cancelCallback?.();
        resolve(false);
      },
    });
  });
};

/**
 * @deprecated use useMessage
 */
export const showSuccessMsg = (
  ctx: ComponentCustomProperties & Record<string, any>,
  msgStr: string,
  durationMs = 3000,
  showClose = false,
  useHTML = false,
  icon = false,
): void => {
  ctx.$message({
    message: msgStr,
    type: 'success',
    duration: durationMs,
    showClose,
    useHTML,
    iconClass: icon ? 'icon-fill-check-circle' : '',
  });
};

/**
 * @deprecated use useMessage
 */
export const showErrorMsg = (
  ctx: ComponentCustomProperties & Record<string, any>,
  msgStr: string,
  durationMs = 3000,
  showClose = false,
  useHTML = false,
  icon = false,
): void => {
  ctx.$message({
    message: msgStr,
    type: 'error',
    duration: durationMs,
    showClose,
    useHTML,
    iconClass: icon ? 'icon-fill-error-circle' : '',
  });
};

/**
 * @deprecated use useMessage
 */
export const showWarningMsg = (
  ctx: ComponentCustomProperties & Record<string, any>,
  msgStr: string,
  durationMs = 3000,
  showClose = false,
  useHTML = false,
  icon = false,
): void => {
  ctx.$message({
    message: msgStr,
    type: 'warning',
    duration: durationMs,
    showClose,
    useHTML,
    iconClass: icon ? 'icon-fill-warning' : '',
  });
};

/**
 * @deprecated use useMessage
 */
export const showInfoMsg = (
  ctx: ComponentCustomProperties & Record<string, any>,
  msgStr: string,
  durationMs = 3000,
  showClose = false,
  icon = false,
): void => {
  ctx.$message({
    message: msgStr,
    type: 'info',
    duration: durationMs,
    showClose,
    iconClass: icon ? 'icon-info-circle' : '',
  });
};

/**
 * @deprecated use useMessage
 */
export const showMsg = (
  ctx: ComponentCustomProperties & Record<string, any>,
  type: 'success' | 'error' | 'warning' | 'info' | 'loading',
  {
    msgStr,
    durationMs = 3000,
    showClose = false,
    useHTML = false,
    icon = false,
    onClose,
  }: {
    msgStr: string;
    durationMs?: number;
    showClose?: boolean;
    useHTML?: boolean;
    icon?: boolean;
    onClose?: () => void;
  },
) => {
  const iconClass = {
    success: 'icon-fill-check-circle',
    error: 'icon-fill-error-circle',
    warning: 'icon-fill-warning',
    info: 'icon-info-circle',
    loading: 'icon-refresh',
  }[type];
  const messageType = type === 'loading' ? 'info' : type;
  return ctx.$message({
    message: msgStr,
    type: messageType,
    duration: durationMs,
    showClose,
    useHTML,
    iconClass: icon ? iconClass : '',
    onClose,
  });
};

export const getErrorMsgKeyOfPromiseReject = (errRes: any) => {
  const { status, data } = errRes ?? {};
  const errorType = data?.errorType || data?.type || 'Exception';
  const isNotFoundTarget = status === 404 && errorType === 'Exception';
  return isNotFoundTarget ? 'MESSAGE.NOT_FOUND_TARGET' : `ERROR.API.${errorType}`;
};

/**
 * @deprecated use useMessage
 */
export const showErrorMsgOfPromiseReject = (
  ctx,
  errRes,
  { useErrorMsgStatus = [] }: { useErrorMsgStatus?: number[] } = {},
) => {
  const { status, data } = errRes ?? {};
  let msg = '';
  if (useErrorMsgStatus.includes(status)) {
    msg = data?.message || i18n.global.t('ERROR.API.Exception');
  } else {
    const errorType = data?.errorType || data?.type || 'Exception';
    const isNotFoundTarget = status === 404 && errorType === 'Exception';
    const msgKey = isNotFoundTarget ? 'MESSAGE.NOT_FOUND_TARGET' : `ERROR.API.${errorType}`;
    msg = i18n.global.t(msgKey);
  }
  showErrorMsg(ctx, msg);
};
